import React, { useEffect, useState } from 'react';
import { MapPinIcon, CalendarIcon, ClockIcon, MountainIcon, UsersIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetDate));
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);
  return timeLeft;
}
function calculateTimeLeft(targetDate: Date) {
  const now = new Date().getTime();
  const target = targetDate.getTime();
  const difference = target - now;
  if (difference <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
  }
  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor(difference / (1000 * 60 * 60) % 24),
    minutes: Math.floor(difference / (1000 * 60) % 60),
    seconds: Math.floor(difference / 1000 % 60)
  };
}
export function RaceInfo() {
  const {
    t
  } = useLanguage();
  const raceDate = new Date('2026-05-16T09:00:00');
  const {
    days,
    hours,
    minutes,
    seconds
  } = useCountdown(raceDate);
  return <section id="info" className="w-full bg-noise py-20 md:py-28 px-4 pt-[10px] pb-[10px]" data-id="element-336">
      <div className="max-w-7xl mx-auto" data-id="element-337">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-338">
          <p className="text-orange-500 font-semibold text-sm uppercase tracking-[0.3em] mb-3" data-id="element-339">
            {t('raceInfo.markCalendar')}
          </p>
          <h2 className="font-display text-4xl md:text-6xl tracking-wider text-white mb-4" data-id="element-340">
            {t('raceInfo.title')}
          </h2>
        </div>

        {/* Countdown Timer */}
        <div className="mb-16" data-id="element-341">
          <p className="text-center text-gray-400 text-sm uppercase tracking-wider mb-6" data-id="element-342">
            {t('raceInfo.startsIn')}
          </p>
          <div className="flex items-center justify-center gap-3 md:gap-6" role="timer" aria-label="Countdown to race start" data-id="element-343">
            <div className="text-center" data-id="element-344">
              <div className="w-20 h-20 md:w-28 md:h-28 bg-neutral-900 border border-neutral-700 rounded-xl flex items-center justify-center glow-orange" data-id="element-345">
                <span className="font-display text-4xl md:text-6xl text-white" data-id="element-346">
                  {String(days).padStart(2, '0')}
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 uppercase tracking-wider mt-2" data-id="element-347">
                {t('raceInfo.days')}
              </p>
            </div>
            <span className="font-display text-3xl md:text-5xl text-orange-500 -mt-6" aria-hidden="true" data-id="element-348">
              :
            </span>
            <div className="text-center" data-id="element-349">
              <div className="w-20 h-20 md:w-28 md:h-28 bg-neutral-900 border border-neutral-700 rounded-xl flex items-center justify-center" data-id="element-350">
                <span className="font-display text-4xl md:text-6xl text-white" data-id="element-351">
                  {String(hours).padStart(2, '0')}
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 uppercase tracking-wider mt-2" data-id="element-352">
                {t('raceInfo.hours')}
              </p>
            </div>
            <span className="font-display text-3xl md:text-5xl text-orange-500 -mt-6" aria-hidden="true" data-id="element-353">
              :
            </span>
            <div className="text-center" data-id="element-354">
              <div className="w-20 h-20 md:w-28 md:h-28 bg-neutral-900 border border-neutral-700 rounded-xl flex items-center justify-center" data-id="element-355">
                <span className="font-display text-4xl md:text-6xl text-white" data-id="element-356">
                  {String(minutes).padStart(2, '0')}
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 uppercase tracking-wider mt-2" data-id="element-357">
                {t('raceInfo.minutes')}
              </p>
            </div>
            <span className="font-display text-3xl md:text-5xl text-orange-500 -mt-6" aria-hidden="true" data-id="element-358">
              :
            </span>
            <div className="text-center" data-id="element-359">
              <div className="w-20 h-20 md:w-28 md:h-28 bg-neutral-900 border border-neutral-700 rounded-xl flex items-center justify-center" data-id="element-360">
                <span className="font-display text-4xl md:text-6xl text-orange-500" data-id="element-361">
                  {String(seconds).padStart(2, '0')}
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 uppercase tracking-wider mt-2" data-id="element-362">
                {t('raceInfo.seconds')}
              </p>
            </div>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-363">
          {/* Race Format */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-364">
            <div className="flex items-center gap-3 mb-4" data-id="element-365">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-366">
                <ClockIcon className="h-5 w-5 text-orange-500" data-id="element-367" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-368">
                {t('raceInfo.raceFormat')}
              </h3>
            </div>
            <p className="text-gray-400 text-sm mb-3" data-id="element-369">
              {t('raceInfo.formatDesc')}
            </p>
          </div>

          {/* Friday Schedule - SuperEnduro */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-370">
            <div className="flex items-center gap-3 mb-4" data-id="element-371">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-372">
                <CalendarIcon className="h-5 w-5 text-orange-500" data-id="element-373" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-374">
                {t('raceInfo.superEnduro')}
              </h3>
            </div>
            <p className="text-gray-300 font-semibold mb-3" data-id="element-375">
              {t('raceInfo.friday')}
            </p>
            <div className="space-y-2 text-sm" data-id="element-376">
              <div className="flex justify-between" data-id="element-377">
                <span className="text-gray-500" data-id="element-378">
                  {t('raceInfo.registration')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-379">15:00</span>
              </div>
              <div className="flex justify-between" data-id="element-380">
                <span className="text-gray-500" data-id="element-381">
                  {t('raceInfo.ridersBriefing')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-382">15:50</span>
              </div>
              <div className="flex justify-between" data-id="element-383">
                <span className="text-gray-500" data-id="element-384">
                  {t('raceInfo.freePractice')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-385">16:00–16:20</span>
              </div>
              <div className="flex justify-between" data-id="element-386">
                <span className="text-gray-500" data-id="element-387">
                  {t('raceInfo.qualification')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-388">16:30–17:30</span>
              </div>
              <div className="flex justify-between" data-id="element-389">
                <span className="text-gray-500" data-id="element-390">
                  {t('raceInfo.lastChance')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-391">17:45–18:00</span>
              </div>
              <div className="flex justify-between" data-id="element-392">
                <span className="text-gray-500" data-id="element-393">
                  {t('raceInfo.semiFinals')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-394">18:00–18:30</span>
              </div>
              <div className="flex justify-between" data-id="element-395">
                <span className="text-orange-500 font-semibold" data-id="element-396">
                  {t('raceInfo.superFinal')}
                </span>
                <span className="text-orange-500 font-bold" data-id="element-397">19:00</span>
              </div>
            </div>
          </div>

          {/* Date & Schedule */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-398">
            <div className="flex items-center gap-3 mb-4" data-id="element-399">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-400">
                <CalendarIcon className="h-5 w-5 text-orange-500" data-id="element-401" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-402">
                {t('raceInfo.hardEnduroRace')}
              </h3>
            </div>
            <p className="text-gray-300 font-semibold mb-3" data-id="element-403">
              {t('raceInfo.saturday')}
            </p>
            <div className="space-y-2 text-sm" data-id="element-404">
              <div className="flex justify-between" data-id="element-405">
                <span className="text-gray-500" data-id="element-406">
                  {t('raceInfo.registration')}{' '}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-407">08:00</span>
              </div>
              <div className="flex justify-between" data-id="element-408">
                <span className="text-gray-500" data-id="element-409">
                  {t('raceInfo.technicalInspection')}{' '}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-410">8:45-11:00</span>
              </div>
              <div className="flex justify-between" data-id="element-411">
                <span className="text-gray-500" data-id="element-412">
                  {t('raceInfo.ridersBriefing')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-413">11:15</span>
              </div>
              <div className="flex justify-between" data-id="element-414">
                <span className="text-orange-500 font-semibold" data-id="element-415">
                  {t('raceInfo.raceStart')}
                </span>
                <span className="text-orange-500 font-bold" data-id="element-416">12:00</span>
              </div>
              <div className="flex justify-between" data-id="element-417">
                <span className="text-gray-500" data-id="element-418">
                  {t('raceInfo.awardCeremonyFull')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-419">17:30</span>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-420">
            <div className="flex items-center gap-3 mb-4" data-id="element-421">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-422">
                <MapPinIcon className="h-5 w-5 text-orange-500" data-id="element-423" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-424">
                {t('raceInfo.location')}
              </h3>
            </div>
            <p className="text-gray-300 font-semibold mb-1" data-id="element-425">
              Pāvuli, Raunas pagasts
            </p>
            <p className="text-gray-500 text-sm mb-3" data-id="element-426">
              Smiltenes novads, Latvija, LV-4131
            </p>
            <p className="text-gray-400 text-sm" data-id="element-427">
              {t('raceInfo.locationDesc')}
            </p>
          </div>

          {/* Terrain */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-428">
            <div className="flex items-center gap-3 mb-4" data-id="element-429">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-430">
                <MountainIcon className="h-5 w-5 text-orange-500" data-id="element-431" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-432">
                {t('raceInfo.terrain')}
              </h3>
            </div>
            <p className="text-gray-400 text-sm mb-3" data-id="element-433">
              {t('raceInfo.terrainDesc')}
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-434">
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-435">
                {t('raceInfo.sandDunes')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-436">
                {t('raceInfo.rockGarden')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-437">
                {t('raceInfo.forestTrails')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-438">
                {t('raceInfo.drops')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-439">
                {t('raceInfo.mudPits')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-440">
                {t('raceInfo.hillClimbs')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-441">
                {t('raceInfo.logObstacles')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-442">
                {t('raceInfo.gravelRoads')}
              </span>
            </div>
          </div>

          {/* Participants */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-443">
            <div className="flex items-center gap-3 mb-4" data-id="element-444">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-445">
                <UsersIcon className="h-5 w-5 text-orange-500" data-id="element-446" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-447">
                {t('raceInfo.participants')}
              </h3>
            </div>
            <p className="text-gray-400 text-sm mb-3" data-id="element-448">
              {t('raceInfo.earlyRegistered')}
            </p>
            <div className="space-y-2 text-sm" data-id="element-449">
              <div className="flex justify-between" data-id="element-450">
                <span className="text-green-500" data-id="element-451">
                  {t('raceInfo.beginners')}
                </span>
                <span className="text-gray-300" data-id="element-452">15 </span>
              </div>
              <div className="flex justify-between" data-id="element-453">
                <span className="text-orange-500" data-id="element-454">{t('raceInfo.hobby')}</span>
                <span className="text-gray-300" data-id="element-455">29</span>
              </div>
              <div className="flex justify-between" data-id="element-456">
                <span className="text-red-500" data-id="element-457">{t('raceInfo.pro')}</span>
                <span className="text-gray-300" data-id="element-458">13</span>
              </div>
              <div className="flex justify-between border-t border-neutral-700 pt-2 mt-2" data-id="element-459">
                <span className="text-white font-semibold" data-id="element-460">
                  {t('raceInfo.total')}
                </span>
                <span className="text-white font-semibold" data-id="element-461">57 braucēji</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}